import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getInitials } from '../utils';
import { getConversations } from '../api/api';

const POLL_INTERVAL_MS = 20000;

const formatTimestamp = (value) => {
  try {
    const date = new Date(value);
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    return sameDay
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch (err) {
    return '';
  }
};

const MessagesInbox = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getToken } = useKindeAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const token = await getToken();
      const data = await getConversations(token);
      setConversations(data || []);
    } catch (err) {
      // keep previous state on transient errors
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
    const interval = setInterval(() => load({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%', p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/')} size="small" aria-label={t('messages.backToHome')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">{t('messages.title')}</Typography>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : conversations.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
          {t('messages.emptyInbox')}
        </Typography>
      ) : (
        <List sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}>
          {conversations.map((c, idx) => {
            const name = `${c.otherUser.firstName || ''} ${c.otherUser.lastName || ''}`.trim();
            return (
              <React.Fragment key={c.otherUser.userId}>
                <ListItemButton
                  onClick={() => navigate(`/messages/${c.otherUser.userId}`)}
                  sx={{ pr: 3 }}
                >
                  <ListItemAvatar>
                    <Badge color="primary" badgeContent={c.unreadCount} overlap="circular">
                      {c.otherUser.hasPicture ? (
                        <Avatar src={c.otherUser.profilePictureUrl} alt={name} />
                      ) : (
                        <Avatar>{getInitials(name || '?')}</Avatar>
                      )}
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography
                          component="span"
                          noWrap
                          sx={{ fontWeight: c.unreadCount > 0 ? 700 : 400, minWidth: 0 }}
                        >
                          {name}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          {formatTimestamp(c.updatedAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{
                          display: 'block',
                          maxWidth: '90%',
                          fontWeight: c.unreadCount > 0 ? 600 : 400
                        }}
                      >
                        {c.lastMessage?.body}
                      </Typography>
                    }
                  />
                </ListItemButton>
                {idx < conversations.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default MessagesInbox;
